<script lang="ts" setup>
import type { BlockPropCustomComponentProps } from '#layers/block-engine/public';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBlockInEditorContext } from '#layers/block-engine/public';
import { useDetailPageContext } from '#layers/blocks/composables/block-context/use-detail-page-context';
import { getCollectionFields, isFieldHidden, sortFieldsBySortKey } from '#layers/data-model/public';
import { ButtonDestructive, showConfirmationDialog } from '#layers/ui/public';
import { useWorkspaceSchemaStore } from '#layers/workspace/public';

const props = defineProps<BlockPropCustomComponentProps>();

/**
 * Ejected
 */
const model = defineModel<boolean>();

const { t } = useI18n();

const { insertBlock, getSlot } = useBlockInEditorContext();
const { getCollection } = useWorkspaceSchemaStore();
const detailPageContext = useDetailPageContext();

const loading = ref(false);

async function insertFormFields() {
	const collectionId = props.child.props.collectionId || detailPageContext.collectionId;

	if (!collectionId) {
		throw new Error('Collection ID is required to insert form fields');
	}

	const collection = getCollection(collectionId);

	if (!collection) {
		throw new Error(`Collection "${collectionId}" not found`);
	}

	const fields = sortFieldsBySortKey(
		getCollectionFields(collection, { includeConstrainedFields: false })
			.filter((field) => !isFieldHidden(field)),
	);

	const children = getSlot('default');

	const missingFields = fields.filter((field) => !children.some((child) => child.props?.fieldId === field.id));

	for (const field of missingFields) {
		await insertBlock({
			block: '$builtin:FormField',
			props: {
				fieldId: field.id,
			},
		});
	}
}

async function eject() {
	if (!await showConfirmationDialog({
		props: {
			title: t('blocks.form.eject_confirm.title'),
			description: t('blocks.form.eject_confirm.description'),
			confirm: {
				label: t('blocks.form.eject_confirm.confirm'),
				color: 'warning',
			},
		},
	})) {
		return;
	}

	model.value = true;
	loading.value = true;

	try {
		await insertFormFields();
	}
	finally {
		loading.value = false;
	}
}

async function cancelEject() {
	if (!await showConfirmationDialog({
		props: {
			title: t('blocks.form.cancel_eject_confirm.title'),
			description: t('blocks.form.cancel_eject_confirm.description'),
			confirm: {
				label: t('blocks.form.cancel_eject_confirm.confirm'),
				color: 'error',
			},
		},
	})) {
		return;
	}

	model.value = false;
}
</script>

<template>
	<DButton
		v-if="loading"
		loading
		block
	/>
	<DButton
		v-else-if="!model"
		:label="$t('blocks.form.eject')"
		icon="lucide:unplug"
		block
		@click="eject()"
	/>
	<div
		v-else
		class="@container"
	>
		<div class="grid grid-cols-1 gap-2 @xs:grid-cols-2">
			<DButton
				:label="$t('blocks.form.refresh_fields')"
				icon="lucide:refresh-cw"
				block
				@click="insertFormFields()"
			/>
			<ButtonDestructive
				:label="$t('blocks.form.cancel_eject')"
				icon="lucide:x"
				variant="outline"
				block
				@click="cancelEject()"
			/>
		</div>
	</div>
</template>
